import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Activity } from 'lucide-react';

/**
 * BootGate — full-screen loader that shows until /api/health returns 200.
 *
 * Why this exists:
 *   • When the hosting platform cold-starts a pod it can inject a
 *     "frontend preview / wake the backend" interstitial before the API
 *     is up.  React itself always mounts, so we aggressively hammer
 *     /api/health until we get a 200 and only THEN reveal the full app.
 *   • If we're still locked out after 30 seconds (likely platform preview
 *     page still covering the DOM), we force a full-page reload — which
 *     usually breaks through the preview once the pod is warm.
 */
const HEALTH_URL = `${process.env.REACT_APP_BACKEND_URL}/api/health`;
const RETRY_INTERVAL_MS = 1500;
const HARD_RELOAD_AFTER_MS = 18000;
const MAX_SOFT_ATTEMPTS = 40;

async function pingHealth(timeoutMs = 4000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const r = await fetch(HEALTH_URL, {
      signal: ctrl.signal,
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!r.ok) return false;
    const j = await r.json().catch(() => null);
    return !!j && (j.status === 'ok' || j.ok === true);
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

export default function BootGate({ children }) {
  const [ready, setReady] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [phase, setPhase] = useState('CONNECTING...');
  const startedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const tryOnce = async () => {
      if (cancelled) return;
      const ok = await pingHealth();
      attempt += 1;
      if (ok) {
        if (!cancelled) setReady(true);
        return;
      }
      setAttempts(attempt);
      if (attempt === 1) setPhase('WAKING BACKEND...');
      else if (attempt >= 5) setPhase('RETRYING...');

      // Hard reload escape: if we've waited 30s and still nothing, force a
      // full-page reload. This dislodges any platform-injected preview HTML.
      const elapsed = Date.now() - startedAt.current;
      if (elapsed > HARD_RELOAD_AFTER_MS && attempt >= MAX_SOFT_ATTEMPTS / 4) {
        try { window.location.reload(); } catch {}
        return;
      }
      setTimeout(tryOnce, RETRY_INTERVAL_MS);
    };
    tryOnce();
    return () => { cancelled = true; };
  }, []);

  if (ready) return children;

  return (
    <div
      data-testid="boot-gate"
      className="min-h-screen bg-[var(--bg-void)] text-cyan-sys flex items-center justify-center"
      style={{ fontFamily: 'Rajdhani, monospace' }}
    >
      <div className="max-w-md px-6 space-y-5">
        <div className="flex items-center gap-3">
          <Activity size={18} className="animate-pulse" />
          <span className="font-display tracking-[0.45em] uppercase text-[14px]">
            ATDE // boot
          </span>
        </div>
        <div className="font-mono text-[12px] text-[var(--txt-secondary)] space-y-1 border-l-2 border-cyan-sys pl-3">
          <div className="text-cyan-sys">{phase}</div>
          <div>// initializing secure channel</div>
          <div>// pinging {HEALTH_URL}</div>
          <div className="text-[var(--txt-muted)]">// attempt {attempts || 1}</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--txt-muted)]">
          <Loader2 size={14} className="animate-spin" />
          <span>auto-reconnect · {(RETRY_INTERVAL_MS / 1000).toFixed(0)}s interval</span>
        </div>
        <div className="text-[9px] tracking-widest uppercase text-[var(--txt-muted)]">
          if this persists 30s → auto-reload
        </div>
      </div>
    </div>
  );
}
